import React, { useState, useRef, useEffect, useCallback } from "react";
import { useGlobalCall } from "./GlobalCallContext";

// ─── Avatar ───────────────────────────────────────────────────────────────────

const Avatar = ({ src, name, size = 56 }: { src?: string | null; name?: string | null; size?: number }) => {
  const valid = src && src !== "null" && src !== "undefined" && !src.includes("default-avatar");
  if (valid) {
    return (
      <img src={src!} alt={name || ""}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, #6c63ff 0%, #3b3086 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, color: "white",
    }}>
      {name?.charAt(0)?.toUpperCase() || "?"}
    </div>
  );
};

// ─── Styles injected once ─────────────────────────────────────────────────────

const STYLES = `
  @keyframes gcw-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(108,99,255,0.5); }
    70%  { box-shadow: 0 0 0 14px rgba(108,99,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(108,99,255,0); }
  }
  @keyframes gcw-ring {
    0%, 100% { transform: rotate(-8deg); }
    50%       { transform: rotate(8deg); }
  }
  @keyframes gcw-fadein {
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes gcw-dot {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40%            { transform: scale(1);   opacity: 1;   }
  }

  .gcw-root {
    position: fixed;
    bottom: 28px;
    right: 28px;
    z-index: 99999;
    font-family: 'Segoe UI', system-ui, sans-serif;
    user-select: none;
    animation: gcw-fadein 0.25s ease;
  }
  .gcw-root.gcw-dragging { cursor: grabbing !important; }

  /* ── Full widget ── */
  .gcw-card {
    width: 260px;
    background: #12121f;
    border-radius: 20px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07);
    overflow: hidden;
    transition: transform 0.15s ease;
  }
  .gcw-card:hover { transform: translateY(-2px); }

  .gcw-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px 0;
  }
  .gcw-header-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
  }
  .gcw-header-actions { display: flex; gap: 6px; }
  .gcw-icon-btn {
    width: 26px; height: 26px; border-radius: 50%;
    border: none; background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.6); font-size: 12px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .gcw-icon-btn:hover { background: rgba(255,255,255,0.16); color: white; }

  .gcw-body {
    padding: 14px 20px 20px;
    text-align: center;
  }
  .gcw-avatar-wrap {
    position: relative;
    display: inline-block;
    margin-bottom: 12px;
  }
  .gcw-avatar-pulse {
    animation: gcw-pulse 1.8s ease-in-out infinite;
    border-radius: 50%;
  }
  .gcw-name {
    font-size: 16px;
    font-weight: 700;
    color: white;
    margin: 0 0 4px;
    letter-spacing: -0.2px;
  }
  .gcw-sub {
    font-size: 11px;
    color: rgba(255,255,255,0.4);
    margin: 0 0 14px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .gcw-duration {
    font-size: 26px;
    font-weight: 300;
    color: white;
    margin: 0 0 18px;
    font-variant-numeric: tabular-nums;
    letter-spacing: 1px;
  }

  /* Typing dots for "calling" state */
  .gcw-dots { display: flex; justify-content: center; gap: 4px; margin-bottom: 18px; }
  .gcw-dot {
    width: 5px; height: 5px; border-radius: 50%;
    background: rgba(255,255,255,0.5);
    animation: gcw-dot 1.4s ease-in-out infinite;
  }
  .gcw-dot:nth-child(2) { animation-delay: 0.2s; }
  .gcw-dot:nth-child(3) { animation-delay: 0.4s; }

  /* Controls */
  .gcw-controls { display: flex; justify-content: center; align-items: center; gap: 14px; }
  .gcw-ctrl {
    display: flex; flex-direction: column; align-items: center; gap: 5px;
  }
  .gcw-ctrl-btn {
    width: 46px; height: 46px; border-radius: 50%;
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; color: white;
    transition: transform 0.12s ease, filter 0.12s ease;
  }
  .gcw-ctrl-btn:hover { transform: scale(1.08); filter: brightness(1.15); }
  .gcw-ctrl-btn:active { transform: scale(0.95); }
  .gcw-ctrl-label {
    font-size: 9px;
    color: rgba(255,255,255,0.4);
    letter-spacing: 0.5px;
  }
  .gcw-btn-mute    { background: rgba(255,255,255,0.1); }
  .gcw-btn-mute.active { background: rgba(255,152,0,0.3); border: 1px solid #ff9800; color: #ff9800; }
  .gcw-btn-hold    { background: rgba(255,255,255,0.1); }
  .gcw-btn-end     { background: #e53935; width: 54px !important; height: 54px !important; font-size: 22px !important; }
  .gcw-btn-accept  { background: #2e7d32; width: 54px !important; height: 54px !important; font-size: 22px !important; }
  .gcw-btn-reject  { background: #e53935; width: 54px !important; height: 54px !important; font-size: 22px !important; }

  /* ── Incoming accept/reject layout ── */
  .gcw-incoming-controls {
    display: flex; justify-content: center; align-items: center; gap: 40px;
    margin-top: 4px;
  }

  /* ── Minimized pill ── */
  .gcw-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #12121f;
    border-radius: 40px;
    padding: 8px 16px 8px 8px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    min-width: 180px;
  }
  .gcw-pill:hover {
    transform: scale(1.03);
    box-shadow: 0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(108,99,255,0.3);
  }
  .gcw-pill-avatar { flex-shrink: 0; }
  .gcw-pill-info { flex: 1; min-width: 0; }
  .gcw-pill-name {
    font-size: 13px; font-weight: 600; color: white;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .gcw-pill-status {
    font-size: 10px; color: #4caf50; margin-top: 1px;
    font-variant-numeric: tabular-nums;
  }
  .gcw-pill-status.ringing { color: rgba(255,255,255,0.4); }
  .gcw-pill-end {
    width: 32px; height: 32px; border-radius: 50%;
    background: #e53935; border: none; color: white;
    font-size: 14px; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    transition: transform 0.12s;
  }
  .gcw-pill-end:hover { transform: scale(1.1); }

  /* Drag handle cursor */
  .gcw-drag-handle { cursor: grab; }
  .gcw-drag-handle:active { cursor: grabbing; }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const style = document.createElement("style");
  style.textContent = STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
}

// ─── Component ────────────────────────────────────────────────────────────────

const GlobalCallWidget: React.FC = () => {
  const {
    callState, isMuted, callDuration, needsManualPlay, currentUserId,
    remoteAudioRef, acceptCall, rejectCall, hangup, toggleMute, manualPlay, formatDuration,
  } = useGlobalCall();

  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { injectStyles(); }, []);

  // Reset minimized state when call ends
  useEffect(() => {
    if (callState.status === "idle" || callState.status === "ended") {
      setMinimized(false);
    }
  }, [callState.status]);


  // ── Dragging ────────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return; // don't drag on buttons
    e.preventDefault();
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: rect.left, origY: rect.top,
    };
    document.body.style.cursor = "grabbing";
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
    };
    const onMouseUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, []);

  // ── Render nothing when idle ────────────────────────────────────────────────
  if (callState.status === "idle" || callState.status === "ended") return null;

  const isIncoming = callState.status === "ringing_in";
  const isRinging = callState.status === "ringing_out";
  const isConnected = callState.status === "connected";

  // Identify who the "other" person is based on current user id
  const isCaller = callState.caller?.id === currentUserId;
  const otherPerson = isCaller ? callState.callee : callState.caller;

  const posStyle: React.CSSProperties = pos
    ? { bottom: "auto", right: "auto", left: pos.x, top: pos.y }
    : {};

  // ── Minimized pill ──────────────────────────────────────────────────────────
  if (minimized) {
    return (
      <>
        {/* Hidden audio element always in DOM during call */}
        <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />

        <div
          ref={rootRef}
          className="gcw-root"
          style={posStyle}
          onMouseDown={onMouseDown}
        >
          <div className="gcw-pill gcw-drag-handle" onClick={() => setMinimized(false)}>
            <div className="gcw-pill-avatar gcw-avatar-pulse">
              <Avatar src={otherPerson?.avatar} name={otherPerson?.name} size={36} />
            </div>
            <div className="gcw-pill-info">
              <div className="gcw-pill-name">{otherPerson?.name}</div>
              <div className={`gcw-pill-status ${isConnected ? "" : "ringing"}`}>
                {isConnected ? `● ${formatDuration(callDuration)}` : "Ringing..."}
              </div>
            </div>
            <button
              className="gcw-pill-end"
              onClick={e => { e.stopPropagation(); hangup(); }}
              title="End call"
            >
              <i className="ti ti-phone-x" />
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Full widget ─────────────────────────────────────────────────────────────
  return (
    <>
      {/* Hidden audio element always in DOM during call */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />

      <div
        ref={rootRef}
        className="gcw-root"
        style={posStyle}
        onMouseDown={onMouseDown}
      >
        <div className="gcw-card gcw-drag-handle">

          {/* Header */}
          <div className="gcw-header">
            <span className="gcw-header-label">
              {isIncoming ? "Incoming call" : isRinging ? "Calling..." : "On call"}
            </span>
            <div className="gcw-header-actions">
              {/* Minimize — only during ringing_out or connected */}
              {(isRinging || isConnected) && (
                <button className="gcw-icon-btn" onClick={() => setMinimized(true)} title="Minimize">
                  <i className="ti ti-minus" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="gcw-body">
            <div className="gcw-avatar-wrap">
              <div className={isIncoming || isRinging ? "gcw-avatar-pulse" : ""}>
                <Avatar src={otherPerson?.avatar} name={otherPerson?.name} size={72} />
              </div>
            </div>

            <p className="gcw-name">{otherPerson?.name || "Unknown"}</p>

            {isConnected ? (
              <>
                <p className="gcw-sub">Connected</p>
                <p className="gcw-duration">{formatDuration(callDuration)}</p>
              </>
            ) : isRinging ? (
              <>
                <p className="gcw-sub">Calling</p>
                <div className="gcw-dots">
                  <div className="gcw-dot" /><div className="gcw-dot" /><div className="gcw-dot" />
                </div>
              </>
            ) : (
              <>
                <p className="gcw-sub">Audio call</p>
                <div className="gcw-dots">
                  <div className="gcw-dot" /><div className="gcw-dot" /><div className="gcw-dot" />
                </div>
              </>
            )}

            {/* Manual play button if autoplay blocked */}
            {needsManualPlay && isConnected && (
              <button
                onClick={manualPlay}
                style={{
                  background: "rgba(255,152,0,0.15)", border: "1px solid #ff9800",
                  color: "#ff9800", padding: "5px 14px", borderRadius: "16px",
                  cursor: "pointer", fontSize: "11px", marginBottom: "12px",
                }}
              >
                🔈 Tap to hear audio
              </button>
            )}

            {/* Controls */}
            {isConnected && (
              <div className="gcw-controls">
                <div className="gcw-ctrl">
                  <button
                    className={`gcw-ctrl-btn gcw-btn-mute ${isMuted ? "active" : ""}`}
                    onClick={toggleMute} title={isMuted ? "Unmute" : "Mute"}
                  >
                    <i className={`ti ${isMuted ? "ti-microphone-off" : "ti-microphone"}`} />
                  </button>
                  <span className="gcw-ctrl-label">{isMuted ? "Unmute" : "Mute"}</span>
                </div>

                <div className="gcw-ctrl">
                  <button className="gcw-ctrl-btn gcw-btn-end" onClick={hangup} title="End call">
                    <i className="ti ti-phone-x" />
                  </button>
                  <span className="gcw-ctrl-label">End</span>
                </div>
              </div>
            )}

            {isRinging && (
              <div className="gcw-controls">
                <div className="gcw-ctrl">
                  <button className="gcw-ctrl-btn gcw-btn-end" onClick={hangup} title="Cancel">
                    <i className="ti ti-phone-x" />
                  </button>
                  <span className="gcw-ctrl-label">Cancel</span>
                </div>
              </div>
            )}

            {isIncoming && (
              <div className="gcw-incoming-controls">
                <div className="gcw-ctrl">
                  <button className="gcw-ctrl-btn gcw-btn-reject" onClick={rejectCall} title="Decline">
                    <i className="ti ti-phone-x" />
                  </button>
                  <span className="gcw-ctrl-label">Decline</span>
                </div>
                <div className="gcw-ctrl">
                  <button className="gcw-ctrl-btn gcw-btn-accept" onClick={acceptCall} title="Accept">
                    <i className="ti ti-phone" />
                  </button>
                  <span className="gcw-ctrl-label">Accept</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalCallWidget;
